import { useState, useEffect, useRef } from 'react'
import { lumi } from '@/lib/lumi'
import { Catador } from '@/types'
import toast from 'react-hot-toast'
import {Plus, Search, Edit2, Trash2, X, Upload, User, Phone, MapPin, Recycle, IdCard, Printer, ChevronDown, ChevronUp, Check} from 'lucide-react'

const TIPOS_RECICLAVEIS = [
  'Papel', 'Papelão', 'Plástico', 'Vidro', 'Metal', 'Alumínio',
  'Eletrônicos', 'Têxtil', 'Orgânico', 'Óleo de Cozinha', 'Outros'
]

const CIDADE_PADRAO = 'São José do Seridó/RN'

const emptyForm = (): Omit<Catador, '_id' | 'creator' | 'createdAt' | 'updatedAt'> => ({
  nomeCompleto: '',
  cpf: '',
  telefone: '',
  rua: '',
  numero: '',
  bairro: '',
  cep: '',
  cidade: CIDADE_PADRAO,
  fotoUrl: '',
  tiposReciclados: [],
  observacoes: '',
  ativo: true,
})

function maskCpf(v: string) {
  return v.replace(/\D/g, '').slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}
function maskTel(v: string) {
  return v.replace(/\D/g, '').slice(0, 11)
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2')
}
function maskCep(v: string) {
  return v.replace(/\D/g, '').slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2')
}

async function buscarCep(cep: string): Promise<string> {
  const raw = cep.replace(/\D/g, '')
  if (raw.length !== 8) return CIDADE_PADRAO
  try {
    const r = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
    const d = await r.json()
    if (d.localidade) return `${d.localidade}/${d.uf}`
    return CIDADE_PADRAO
  } catch { return CIDADE_PADRAO }
}

/* ──────────────── Carteirinha ──────────────── */
function CarteirinhaPreview({ catador, onClose }: { catador: Catador; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null)

  const imprimir = () => {
    const el = ref.current
    if (!el) return
    const w = window.open('', '_blank', 'width=700,height=420')
    if (!w) return
    w.document.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <title>Carteirinha – ${catador.nomeCompleto}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap');
        body { margin:0; background:#f1f5f9; display:flex; justify-content:center; align-items:center; min-height:100vh; font-family:'Inter',sans-serif; }
        .card { width:440px; border-radius:20px; overflow:hidden; box-shadow:0 8px 32px rgba(0,0,0,.18); }
        @media print { body { background:white; } .no-print { display:none; } }
      </style>
    </head><body>
      ${el.outerHTML}
      <script>window.onload=()=>{ setTimeout(()=>{ window.print(); }, 300); }<\/script>
    </body></html>`)
    w.document.close()
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#0f3b50]">Carteirinha do Catador</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-2xl transition"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-8 flex flex-col items-center gap-6">
          {/* cartão para impressão */}
          <div ref={ref} className="card" style={{ width: 440, fontFamily: "'Inter',sans-serif", borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,.15)' }}>
            {/* header */}
            <div style={{ background: '#0f3b50', padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 19H4.815a1.83 1.83 0 0 1-1.57-.881 1.785 1.785 0 0 1-.004-1.784L7.196 9.5"/><path d="M11 19h8.203a1.83 1.83 0 0 0 1.556-.89 1.784 1.784 0 0 0 0-1.775l-1.226-2.12"/><path d="m14 16-3 3 3 3"/><path d="M8.293 13.596 7.196 9.5 3.1 10.598"/><path d="m9.344 5.811 1.093-1.892A1.83 1.83 0 0 1 11.985 3a1.784 1.784 0 0 1 1.546.888l3.943 6.843"/><path d="m13.378 9.633 4.096 1.098 1.097-4.096"/></svg>
              </div>
              <div>
                <div style={{ color: 'white', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>GEOA – Gestão Online Ambiental</div>
                <div style={{ color: 'rgba(255,255,255,.7)', fontSize: 11, marginTop: 2 }}>Prefeitura Municipal de São José do Seridó/RN</div>
              </div>
              <div style={{ marginLeft: 'auto', background: 'rgba(255,255,255,.15)', borderRadius: 8, padding: '4px 10px', color: 'white', fontSize: 11, fontWeight: 600 }}>CATADOR</div>
            </div>
            {/* body */}
            <div style={{ background: 'white', padding: '20px 24px', display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              {/* foto */}
              <div style={{ flexShrink: 0 }}>
                {catador.fotoUrl
                  ? <img src={catador.fotoUrl} alt="foto" style={{ width: 88, height: 88, borderRadius: 16, objectFit: 'cover', border: '3px solid #e2e8f0' }} />
                  : <div style={{ width: 88, height: 88, borderRadius: 16, background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    </div>
                }
              </div>
              {/* dados */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#0f3b50', lineHeight: 1.2, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{catador.nomeCompleto}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>CPF: {catador.cpf}</div>
                <div style={{ fontSize: 12, color: '#475569', marginBottom: 4 }}>📞 {catador.telefone}</div>
                <div style={{ fontSize: 12, color: '#475569', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  📍 {catador.rua}, {catador.numero} – {catador.bairro}
                </div>
                {/* tipos */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {catador.tiposReciclados.map(t => (
                    <span key={t} style={{ background: '#eff8ff', color: '#0f3b50', border: '1px solid #bae6fd', borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
            {/* footer */}
            <div style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>Cadastrado em: {new Date(catador.createdAt).toLocaleDateString('pt-BR')}</span>
              <span style={{ fontSize: 10, color: catador.ativo ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{catador.ativo ? '● ATIVO' : '● INATIVO'}</span>
            </div>
          </div>

          <div className="flex gap-3 no-print">
            <button onClick={imprimir} className="flex items-center gap-2 px-6 py-2.5 bg-[#0f3b50] text-white text-sm font-semibold rounded-2xl hover:bg-[#0c2e3e] transition shadow-sm">
              <Printer className="w-4 h-4" /> Imprimir Carteirinha
            </button>
            <button onClick={onClose} className="flex items-center gap-2 px-6 py-2.5 border border-gray-200 text-gray-600 text-sm font-semibold rounded-2xl hover:bg-gray-50 transition">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ──────────────── Modal Formulário ──────────────── */
function ModalCatador({
  catador, onClose, onSaved
}: { catador: Catador | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<Omit<Catador, '_id' | 'creator' | 'createdAt' | 'updatedAt'>>(
    catador ? {
      nomeCompleto: catador.nomeCompleto,
      cpf: catador.cpf,
      telefone: catador.telefone,
      rua: catador.rua,
      numero: catador.numero,
      bairro: catador.bairro,
      cep: catador.cep,
      cidade: catador.cidade,
      fotoUrl: catador.fotoUrl || '',
      tiposReciclados: catador.tiposReciclados || [],
      observacoes: catador.observacoes || '',
      ativo: catador.ativo,
    } : emptyForm()
  )
  const [saving, setSaving] = useState(false)
  const [uploadingFoto, setUploadingFoto] = useState(false)
  const [buscandoCep, setBuscandoCep] = useState(false)
  const fotoInputRef = useRef<HTMLInputElement>(null)

  const set = (k: keyof typeof form, v: unknown) => setForm(p => ({ ...p, [k]: v }))

  const handleCep = async (raw: string) => {
    const masked = maskCep(raw)
    set('cep', masked)
    if (masked.replace(/\D/g, '').length === 8) {
      setBuscandoCep(true)
      const cidade = await buscarCep(masked)
      set('cidade', cidade)
      setBuscandoCep(false)
    }
  }

  const toggleTipo = (t: string) => {
    setForm(p => ({
      ...p,
      tiposReciclados: p.tiposReciclados.includes(t)
        ? p.tiposReciclados.filter(x => x !== t)
        : [...p.tiposReciclados, t]
    }))
  }

  const handleFoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingFoto(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const url = results[0]?.fileUrl
      if (url) set('fotoUrl', url)
      else toast.error('Erro ao fazer upload da foto.')
    } catch {
      toast.error('Erro ao fazer upload da foto.')
    } finally {
      setUploadingFoto(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.nomeCompleto || !form.cpf || !form.telefone || !form.rua || !form.bairro) {
      toast.error('Preencha todos os campos obrigatórios.')
      return
    }
    if (form.tiposReciclados.length === 0) {
      toast.error('Seleccione pelo menos um tipo de reciclável.')
      return
    }
    setSaving(true)
    const now = new Date().toISOString()
    try {
      if (catador) {
        await lumi.entities.catadores.update(catador._id, { ...form, updatedAt: now })
        toast.success('Catador actualizado com sucesso!')
      } else {
        await lumi.entities.catadores.create({ ...form, creator: 'admin', createdAt: now, updatedAt: now })
        toast.success('Catador cadastrado com sucesso!')
      }
      onSaved()
    } catch {
      toast.error('Erro ao guardar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#0f3b50]">{catador ? 'Editar Catador' : 'Novo Catador'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-2xl transition"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Foto */}
          <div className="flex flex-col items-center gap-3">
            <div
              onClick={() => fotoInputRef.current?.click()}
              className="relative w-24 h-24 rounded-3xl border-2 border-dashed border-gray-200 overflow-hidden cursor-pointer hover:border-[#0f3b50] hover:bg-blue-50 transition flex items-center justify-center bg-gray-50"
            >
              {uploadingFoto
                ? <div className="w-8 h-8 border-2 border-[#0f3b50] border-t-transparent rounded-full animate-spin" />
                : form.fotoUrl
                  ? <img src={form.fotoUrl} alt="foto" className="w-full h-full object-cover" />
                  : <User className="w-10 h-10 text-gray-300" />
              }
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition flex items-end justify-center pb-2">
                <span className="text-[10px] text-white font-semibold bg-black/40 rounded px-1 opacity-0 hover:opacity-100 transition">Alterar</span>
              </div>
            </div>
            <input ref={fotoInputRef} type="file" accept="image/*" className="hidden" onChange={handleFoto} />
            <span className="text-xs text-gray-400">Clique para adicionar foto</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nome Completo *</label>
              <input value={form.nomeCompleto} onChange={e => set('nomeCompleto', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0f3b50]/30 focus:border-[#0f3b50]"
                placeholder="Nome completo do catador" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">CPF *</label>
              <input value={form.cpf} onChange={e => set('cpf', maskCpf(e.target.value))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0f3b50]/30 focus:border-[#0f3b50]"
                placeholder="000.000.000-00" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefone *</label>
              <input value={form.telefone} onChange={e => set('telefone', maskTel(e.target.value))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0f3b50]/30 focus:border-[#0f3b50]"
                placeholder="(00) 00000-0000" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">CEP</label>
              <div className="relative">
                <input value={form.cep} onChange={e => handleCep(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0f3b50]/30 focus:border-[#0f3b50]"
                  placeholder="00000-000" />
                {buscandoCep && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-[#0f3b50] border-t-transparent rounded-full animate-spin" />}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Cidade</label>
              <input value={form.cidade} readOnly
                className="w-full px-4 py-2.5 border border-gray-100 rounded-2xl text-sm bg-gray-50 text-gray-500" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Rua / Logradouro *</label>
              <input value={form.rua} onChange={e => set('rua', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0f3b50]/30 focus:border-[#0f3b50]"
                placeholder="Nome da rua" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Número *</label>
              <input value={form.numero} onChange={e => set('numero', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0f3b50]/30 focus:border-[#0f3b50]"
                placeholder="Nº" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Bairro *</label>
              <input value={form.bairro} onChange={e => set('bairro', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0f3b50]/30 focus:border-[#0f3b50]"
                placeholder="Bairro" required />
            </div>
          </div>

          {/* Tipos de recicláveis */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Tipos de Recicláveis *</label>
            <div className="flex flex-wrap gap-2">
              {TIPOS_RECICLAVEIS.map(t => (
                <button type="button" key={t} onClick={() => toggleTipo(t)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
                    form.tiposReciclados.includes(t)
                      ? 'bg-[#0f3b50] text-white border-[#0f3b50]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#0f3b50] hover:text-[#0f3b50]'
                  }`}>
                  {form.tiposReciclados.includes(t) && <Check className="w-3 h-3" />}
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Observações</label>
            <textarea value={form.observacoes} onChange={e => set('observacoes', e.target.value)} rows={2}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0f3b50]/30 focus:border-[#0f3b50] resize-none"
              placeholder="Observações adicionais (opcional)" />
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={() => set('ativo', !form.ativo)}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.ativo ? 'bg-[#0f3b50]' : 'bg-gray-200'}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.ativo ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
            <span className="text-sm text-gray-600">{form.ativo ? 'Catador Activo' : 'Catador Inactivo'}</span>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-semibold text-gray-600 border border-gray-200 rounded-2xl hover:bg-gray-50 transition">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2.5 bg-[#0f3b50] hover:bg-[#0c2e3e] disabled:opacity-50 text-white text-sm font-semibold rounded-2xl transition shadow-sm">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
              {saving ? 'A guardar…' : catador ? 'Guardar Alterações' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ──────────────── Page principal ──────────────── */
export default function CatadoresPage() {
  const [catadores, setCatadores] = useState<Catador[]>([])
  const [loading, setLoading] = useState(true)
  const [busca, setBusca] = useState('')
  const [modal, setModal] = useState(false)
  const [editando, setEditando] = useState<Catador | null>(null)
  const [carteirinha, setCarteirinha] = useState<Catador | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Catador | null>(null)
  const [deletando, setDeletando] = useState(false)

  const carregar = async () => {
    setLoading(true)
    try {
      const res = await lumi.entities.catadores.list({ limit: 9999, sort: { createdAt: -1 } })
      setCatadores((res?.list || []) as Catador[])
    } catch {
      toast.error('Erro ao carregar catadores.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { carregar() }, [])

  const filtrados = catadores.filter(c =>
    c.nomeCompleto?.toLowerCase().includes(busca.toLowerCase()) ||
    c.cpf?.includes(busca) ||
    c.bairro?.toLowerCase().includes(busca.toLowerCase())
  )

  const excluir = async () => {
    if (!confirmDelete) return
    setDeletando(true)
    try {
      await lumi.entities.catadores.delete(confirmDelete._id)
      toast.success('Catador removido.')
      setConfirmDelete(null)
      await carregar()
    } catch {
      toast.error('Erro ao remover catador.')
    } finally {
      setDeletando(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0f3b50]">Catadores</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gestão dos catadores do município</p>
        </div>
        <button onClick={() => { setEditando(null); setModal(true) }}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#0f3b50] hover:bg-[#0c2e3e] text-white text-sm font-semibold rounded-2xl transition shadow-sm">
          <Plus className="w-4 h-4" /> Novo Catador
        </button>
      </div>

      {/* Busca + stats */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-4 flex flex-col sm:flex-row gap-3 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={busca} onChange={e => setBusca(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0f3b50]/20 focus:border-[#0f3b50]"
            placeholder="Buscar por nome, CPF ou bairro…" />
        </div>
        <div className="flex gap-3 text-sm text-gray-500">
          <span className="bg-[#eff8ff] text-[#0f3b50] font-semibold px-3 py-1.5 rounded-xl">{filtrados.length} catadores</span>
          <span className="bg-green-50 text-green-700 font-semibold px-3 py-1.5 rounded-xl">{filtrados.filter(c => c.ativo).length} activos</span>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 border-3 border-[#0f3b50] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtrados.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-12 text-center">
          <Recycle className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{busca ? 'Nenhum catador encontrado para esta pesquisa.' : 'Nenhum catador cadastrado ainda.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtrados.map(c => (
            <div key={c._id} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center gap-4 p-4">
                {/* foto */}
                <div className="w-12 h-12 rounded-2xl overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                  {c.fotoUrl
                    ? <img src={c.fotoUrl} alt={c.nomeCompleto} className="w-full h-full object-cover" />
                    : <User className="w-6 h-6 text-gray-300" />
                  }
                </div>
                {/* info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#0f3b50] text-sm truncate">{c.nomeCompleto}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-lg ${c.ativo ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                      {c.ativo ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>CPF: {c.cpf}</span>
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.telefone}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.bairro}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {c.tiposReciclados?.slice(0, 3).map(t => (
                      <span key={t} className="text-[10px] font-semibold px-2 py-0.5 bg-blue-50 text-[#0f3b50] rounded-lg border border-blue-100">{t}</span>
                    ))}
                    {(c.tiposReciclados?.length || 0) > 3 && (
                      <span className="text-[10px] text-gray-400 px-2 py-0.5">+{c.tiposReciclados.length - 3}</span>
                    )}
                  </div>
                </div>
                {/* acções */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => setCarteirinha(c)} title="Ver Carteirinha"
                    className="p-2 text-[#0f3b50] hover:bg-blue-50 rounded-xl transition">
                    <IdCard className="w-4 h-4" />
                  </button>
                  <button onClick={() => { setEditando(c); setModal(true) }} title="Editar"
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-xl transition">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setConfirmDelete(c)} title="Excluir"
                    className="p-2 text-red-400 hover:bg-red-50 rounded-xl transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setExpandedId(expandedId === c._id ? null : c._id)}
                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-xl transition">
                    {expandedId === c._id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {/* expandido */}
              {expandedId === c._id && (
                <div className="border-t border-gray-50 px-6 py-4 bg-gray-50/50 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Endereço completo</span><p className="text-gray-700 mt-0.5">{c.rua}, {c.numero} – {c.bairro}, {c.cidade} – CEP {c.cep}</p></div>
                  <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Recicláveis coletados</span><p className="text-gray-700 mt-0.5">{c.tiposReciclados?.join(', ') || '—'}</p></div>
                  {c.observacoes && <div className="sm:col-span-2"><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Observações</span><p className="text-gray-700 mt-0.5">{c.observacoes}</p></div>}
                  <div><span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Cadastrado em</span><p className="text-gray-700 mt-0.5">{new Date(c.createdAt).toLocaleDateString('pt-BR')}</p></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal formulário */}
      {modal && (
        <ModalCatador
          catador={editando}
          onClose={() => { setModal(false); setEditando(null) }}
          onSaved={() => { setModal(false); setEditando(null); carregar() }}
        />
      )}

      {/* Carteirinha */}
      {carteirinha && <CarteirinhaPreview catador={carteirinha} onClose={() => setCarteirinha(null)} />}

      {/* Confirm delete */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-50 rounded-3xl flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Remover Catador?</h3>
            <p className="text-sm text-gray-500 mb-6">Esta acção não pode ser desfeita. O catador <strong>{confirmDelete.nomeCompleto}</strong> será permanentemente removido.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-semibold rounded-2xl hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={excluir} disabled={deletando} className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-semibold rounded-2xl transition">
                {deletando ? 'A remover…' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
